import { useRef, useEffect } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import MicOffIcon from '@mui/icons-material/MicOff';
import PanToolIcon from '@mui/icons-material/PanTool';
import useVideoDelay from '../../hooks/useVideoDelay';
import { getParticipantColor } from '../../utils/participantColors';
import { keyframes } from '@mui/system';

// Translation pipeline delay: STT(500ms) + LLM(300ms) + TTS(400ms) + buffer margin
const TRANSLATION_DELAY_MS = 3000;

// Pulse animation for active speaker
const speakingPulse = keyframes`
  0% { box-shadow: 0 0 0 0 var(--speaker-color); }
  50% { box-shadow: 0 0 20px 5px var(--speaker-color); }
  100% { box-shadow: 0 0 0 0 var(--speaker-color); }
`;

function VideoTile({ stream, participant, isLocal, audioEnabled, videoEnabled, isSpeaking, shouldDelayVideo, participantColor }) {
  const videoRef = useRef(null);
  const hiddenVideoRef = useRef(null);
  const audioRef = useRef(null);

  // Use video delay when this participant is sending us translated audio
  const shouldDelay = shouldDelayVideo;
  const { setSourceVideo, canvasRef, isBuffering } = useVideoDelay(shouldDelay, TRANSLATION_DELAY_MS);

  // Connect stream to hidden video for delay capture
  useEffect(() => {
    if (!stream) return;
    if (hiddenVideoRef.current) {
      hiddenVideoRef.current.srcObject = stream;
      setSourceVideo(hiddenVideoRef.current);
    }
  }, [stream, setSourceVideo]);

  // Connect stream to main video element (separate effect to avoid flicker on audio toggle)
  useEffect(() => {
    if (!stream || !videoEnabled) return;
    if (videoRef.current) {
      const tracks = stream.getVideoTracks();
      console.log(`[VideoGrid] Setting srcObject for ${isLocal ? 'LOCAL' : participant?.displayName}:`,
        'tracks:', tracks.length,
        'state:', tracks[0]?.readyState,
        'enabled:', tracks[0]?.enabled);
      videoRef.current.srcObject = stream;

      // Force play for canvas-based streams
      videoRef.current.play().catch(err => {
        console.warn('[VideoGrid] play() failed:', err.message);
      });

      // Log video element state after a short delay
      setTimeout(() => {
        if (videoRef.current) {
          console.log(`[VideoGrid] Video element state for ${isLocal ? 'LOCAL' : participant?.displayName}:`,
            'paused:', videoRef.current.paused,
            'readyState:', videoRef.current.readyState,
            'videoWidth:', videoRef.current.videoWidth,
            'videoHeight:', videoRef.current.videoHeight);
        }
      }, 500);
    }
  }, [stream, videoEnabled, isLocal, participant?.displayName]);

  // Connect audio for remote participants (separate effect to avoid video flicker)
  useEffect(() => {
    if (!stream || isLocal || !audioEnabled) return;
    if (audioRef.current) {
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length > 0) {
        const audioStream = new MediaStream(audioTracks);
        audioRef.current.srcObject = audioStream;
        console.log(`[VideoGrid] Connected audio for ${participant?.displayName}, tracks:`, audioTracks.length);
      }
    }
  }, [stream, isLocal, audioEnabled, participant?.displayName]);

  const displayName = participant?.displayName || 'Unknown';
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <Box
      sx={{
        '--speaker-color': participantColor?.border || '#25D366',
        position: 'relative',
        width: '100%',
        height: '100%',
        bgcolor: 'surface.main',
        borderRadius: 2,
        overflow: 'hidden',
        border: isSpeaking ? '3px solid' : '2px solid',
        borderColor: isSpeaking ? participantColor?.border : `${participantColor?.border}40`,
        transition: 'all 0.3s ease',
        animation: isSpeaking ? `${speakingPulse} 1.5s ease-in-out infinite` : 'none',
      }}
    >
      {/* Hidden video source for delay capture - always rendered so ref is available */}
      <video
        ref={hiddenVideoRef}
        autoPlay
        playsInline
        muted
        style={{ display: 'none' }}
      />

      {/* Separate audio element for remote participants - only when audio is enabled (security) */}
      {!isLocal && audioEnabled && (
        <audio
          ref={audioRef}
          autoPlay
          playsInline
        />
      )}

      {/* Video element - only mounted when video is enabled (security: prevents DevTools manipulation) */}
      {videoEnabled && stream && (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: isLocal ? 'scaleX(-1)' : 'none',
              // Hide when delay is active and buffer is ready
              display: (shouldDelay && isBuffering) ? 'none' : 'block',
            }}
          />

          {/* Delayed video via canvas - shown only when delay is active and buffer is ready */}
          {shouldDelay && isBuffering && (
            <canvas
              ref={canvasRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}
        </>
      )}

      {/* Avatar - shown when video is disabled or no stream */}
      {(!videoEnabled || !stream) && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'surface.dark',
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              fontSize: 32,
              bgcolor: 'primary.main',
            }}
          >
            {initials}
          </Avatar>
        </Box>
      )}

      {/* Name badge with color indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            bgcolor: 'rgba(0,0,0,0.7)',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            borderLeft: `3px solid ${participantColor?.border || '#25D366'}`,
          }}
        >
          {!audioEnabled && <MicOffIcon sx={{ fontSize: 16, color: 'error.main' }} />}
          <Typography variant="body2" noWrap sx={{ color: 'white' }}>
            {displayName} {isLocal && '(You)'}
          </Typography>
        </Box>
      </Box>

      {/* Hand raised indicator */}
      {participant?.handRaised && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'warning.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            animation: 'pulse 1s infinite',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.1)' },
              '100%': { transform: 'scale(1)' },
            },
          }}
        >
          <PanToolIcon sx={{ fontSize: 20 }} />
        </Box>
      )}

      {/* Host badge */}
      {participant?.isHost && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            bgcolor: 'primary.main',
            borderRadius: 1,
            px: 1,
            py: 0.25,
          }}
        >
          <Typography variant="caption">Host</Typography>
        </Box>
      )}

    </Box>
  );
}

export default function VideoGrid({
  localStream,
  remoteStreams,
  participants,
  localParticipant,
  audioEnabled,
  videoEnabled,
  translatingPeers = new Set(),
  activeSpeaker = null,
}) {
  console.log('VideoGrid - participants:', participants.map(p => ({ socketId: p.socketId, name: p.displayName })));
  console.log('VideoGrid - remoteStreams keys:', Array.from(remoteStreams.keys()));

  const allParticipants = [
    { ...localParticipant, isLocal: true, stream: localStream, audioEnabled, videoEnabled },
    ...participants.map((p) => {
      const stream = remoteStreams.get(p.socketId);
      console.log(`VideoGrid - participant ${p.displayName} socketId=${p.socketId}, hasStream=${!!stream}`);
      return {
        ...p,
        isLocal: false,
        stream: stream,
      };
    }),
  ];

  // Pre-assign colors to all participants for consistency
  const localId = localParticipant?.socketId;

  const count = allParticipants.length;

  // Calculate grid layout
  const getGridTemplate = () => {
    if (count === 1) return { columns: 1, rows: 1 };
    if (count === 2) return { columns: 2, rows: 1 };
    if (count <= 4) return { columns: 2, rows: 2 };
    if (count <= 6) return { columns: 3, rows: 2 };
    if (count <= 9) return { columns: 3, rows: 3 };
    return { columns: 4, rows: Math.ceil(count / 4) };
  };

  const { columns, rows } = getGridTemplate();

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: 1,
        p: 1,
      }}
    >
      {allParticipants.map((participant, index) => {
        // Delay video if this remote peer is sending us translated audio
        const shouldDelayVideo = !participant.isLocal && translatingPeers.has(participant.socketId);
        const participantId = participant.isLocal ? localId : participant.socketId;
        const participantColor = getParticipantColor(participantId, participant.isLocal);
        const isSpeaking = activeSpeaker === participantId;

        return (
          <VideoTile
            key={participant.isLocal ? 'local' : participant.socketId || index}
            stream={participant.stream}
            participant={participant}
            isLocal={participant.isLocal}
            audioEnabled={participant.audioEnabled}
            videoEnabled={participant.videoEnabled}
            shouldDelayVideo={shouldDelayVideo}
            participantColor={participantColor}
            isSpeaking={isSpeaking}
          />
        );
      })}
    </Box>
  );
}
